import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGame, getGameState, performAction, chooseNoble } from '../api/games';
import { useGameWebSocket } from '../hooks/useWebSocket';
import GameBoard from '../components/GameBoard';

export default function GamePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gameMeta, setGameMeta] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [stateVersion, setStateVersion] = useState(0);
  const [pageError, setPageError] = useState('');
  const [actionError, setActionError] = useState('');

  const loadAll = useCallback(async () => {
    try {
      const [metaRes, stateRes] = await Promise.all([
        getGame(id),
        getGameState(id),
      ]);
      setGameMeta(metaRes.data);
      setGameState(stateRes.data.state);
      setStateVersion(stateRes.data.state_version);
      // Redirect if game not yet started
      if (metaRes.data.status === 'waiting' || metaRes.data.status === 'ready') {
        navigate(`/games/${id}/room`);
      }
    } catch {
      setPageError('加载游戏失败，请检查后端是否运行');
    }
  }, [id, navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // WebSocket: receive live state updates from other players' moves
  const handleWsMessage = useCallback((data) => {
    if (data.type === 'state.update') {
      if (data.state) {
        setGameState(data.state);
        setStateVersion(data.state_version);
      } else {
        loadAll();
      }
    }
    if (data.game_status) {
      setGameMeta(prev => prev ? { ...prev, status: data.game_status } : prev);
    }
  }, [loadAll]);

  const { closeCode } = useGameWebSocket(id, handleWsMessage, { onOpen: loadAll });

  useEffect(() => {
    if (closeCode === 4001) {
      setPageError('实时连接认证失败，请重新登录');
    } else if (closeCode === 4003) {
      setPageError('你不在这局游戏中，无法接收实时更新');
    }
  }, [closeCode]);

  const handleAction = async (type, payload) => {
    setActionError('');
    try {
      const { data } = await performAction(id, type, payload, stateVersion);
      setGameState(data.state);
      setStateVersion(data.state_version);
      if (data.game_status) setGameMeta(prev => ({ ...prev, status: data.game_status }));
      return data;
    } catch (err) {
      const msg = err.response?.data?.detail || '操作失败';
      setActionError(msg);
      // On version conflict, re-sync state from server
      if (err.response?.status === 409) loadAll();
      throw err;
    }
  };

  const handleChooseNoble = async (nobleId) => {
    setActionError('');
    try {
      const { data } = await chooseNoble(id, nobleId, stateVersion);
      setGameState(data.state);
      setStateVersion(data.state_version);
      if (data.game_status) setGameMeta(prev => ({ ...prev, status: data.game_status }));
      return data;
    } catch (err) {
      setActionError(err.response?.data?.detail || '操作失败');
      throw err;
    }
  };

  if (pageError) return <div className="loading-page">{pageError}</div>;
  if (!gameMeta || !gameState) return <div className="loading-page">加载游戏中...</div>;

  return (
    <GameBoard
      gameMeta={gameMeta}
      gameState={gameState}
      currentUserId={Number(user?.id)}
      onAction={handleAction}
      onChooseNoble={handleChooseNoble}
      onLeave={() => navigate('/lobby')}
      actionError={actionError}
    />
  );
}
